import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Loader2, Package, PackagePlus, Zap, Search } from 'lucide-react';
import { toast } from 'sonner';

export default function ScorteValorizza() {
  const [dati, setDati] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(null);
  const [standard, setStandard] = useState(false);

  const fetchDati = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/scorte/da-valorizzare');
      setDati(r.data);
    } catch { toast.error('Errore caricamento scorte'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchDati(); }, [fetchDati]);

  const valorizza = async (t) => {
    const f = form[t.id] || {};
    const unita = parseInt(f.unita ?? t.unita_confezione_suggerita) || 0;
    const scatole = parseInt(f.scatole ?? 1) || 1;
    if (unita <= 0) { toast.error('Indica le unità per confezione'); return; }
    setSaving(t.id);
    try {
      const r = await api.put(`/scorte/${t.id}/valorizza`, { scatole, unita_confezione: unita });
      toast.success(`${t.farmaco}: ${r.data.scorta_attuale} unità in magazzino`);
      setDati(d => ({
        ...d,
        totale_terapie: d.totale_terapie - 1,
        gruppi: d.gruppi.map(g => ({ ...g, terapie: g.terapie.filter(x => x.id !== t.id) })).filter(g => g.terapie.length)
      }));
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore valorizzazione'); }
    setSaving(null);
  };

  const valorizzaStandard = async (ospiteId) => {
    setStandard(true);
    try {
      const r = await api.post('/scorte/valorizza-standard', { giorni: 30, ospite_id: ospiteId || '' });
      toast.success(`${r.data.terapie_valorizzate} terapie valorizzate con scorta stimata di 30 giorni`);
      fetchDati();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
    setStandard(false);
  };

  const gruppi = (dati?.gruppi || []).filter(g =>
    `${g.ospite.cognome} ${g.ospite.nome}`.toLowerCase().includes(q.toLowerCase()));

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-brand" /></div>;

  return (
    <div className="space-y-5" data-testid="scorte-valorizza-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-800 font-[Cinzel]">Scorte da valorizzare</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            {dati?.totale_terapie || 0} terapie senza magazzino su {dati?.totale_ospiti || 0} ospiti ·
            {dati?.terapie_valorizzate || 0} già valorizzate · si parte dagli ospiti con più farmaci
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
            <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Cerca ospite"
              className="h-9 pl-8 w-48" data-testid="scorte-search" />
          </div>
          <Button size="sm" variant="outline" className="h-9" disabled={standard}
            onClick={() => valorizzaStandard('')} data-testid="valorizza-standard-tutti">
            <Zap className="w-4 h-4 mr-1" />Scorta stimata 30 giorni (tutti)
          </Button>
        </div>
      </div>

      {gruppi.length === 0 && (
        <Card><CardContent className="p-8 text-center text-sm text-slate-400">
          Tutte le terapie hanno il magazzino valorizzato.
        </CardContent></Card>
      )}

      {gruppi.map(g => (
        <Card key={g.ospite.id} data-testid={`scorte-gruppo-${g.ospite.id}`}>
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Package className="w-4 h-4 text-brand" />
              {g.ospite.cognome} {g.ospite.nome}
              <span className="text-[11px] font-normal text-slate-400">
                stanza {g.ospite.stanza || '-'} · {g.terapie.length} farmaci
              </span>
            </CardTitle>
            <Button size="sm" variant="ghost" className="h-7 text-[11px]" disabled={standard}
              onClick={() => valorizzaStandard(g.ospite.id)} data-testid={`standard-${g.ospite.id}`}>
              Stima 30 giorni
            </Button>
          </CardHeader>
          <CardContent className="space-y-2">
            {g.terapie.map(t => {
              const f = form[t.id] || {};
              return (
                <div key={t.id} className="flex flex-wrap items-center gap-2 p-2 rounded-lg border border-slate-200"
                  data-testid={`scorta-riga-${t.id}`}>
                  <div className="min-w-[210px] flex-1">
                    <p className="text-sm font-medium text-slate-800">{t.farmaco}</p>
                    <p className="text-[11px] text-slate-400">
                      {t.dosaggio} · {t.via}
                      {t.al_bisogno ? ' · al bisogno' : ` · ${t.somministrazioni_giorno}/die (${t.orari.join(', ')})`}
                    </p>
                  </div>
                  <div className="flex items-end gap-2">
                    <div>
                      <p className="text-[10px] text-slate-500">Scatole</p>
                      <Input type="number" min="1" className="h-8 w-20 text-xs" placeholder="1"
                        value={f.scatole ?? ''} onChange={e => setForm(s => ({ ...s, [t.id]: { ...f, scatole: e.target.value } }))}
                        data-testid={`scatole-${t.id}`} />
                    </div>
                    <div>
                      <p className="text-[10px] text-slate-500">Unità/conf.</p>
                      <Input type="number" min="1" className="h-8 w-24 text-xs"
                        placeholder={t.unita_confezione_suggerita || 'es. 30'}
                        value={f.unita ?? ''} onChange={e => setForm(s => ({ ...s, [t.id]: { ...f, unita: e.target.value } }))}
                        data-testid={`unita-${t.id}`} />
                    </div>
                    <Button size="sm" className="h-8 bg-brand hover:bg-brand-dark text-white"
                      disabled={saving === t.id} onClick={() => valorizza(t)} data-testid={`valorizza-${t.id}`}>
                      <PackagePlus className="w-3.5 h-3.5 mr-1" />{saving === t.id ? '...' : 'Registra'}
                    </Button>
                  </div>
                  {t.unita_confezione_suggerita > 0 && (
                    <Badge variant="outline" className="text-[10px]">conf. standard {t.unita_confezione_suggerita}</Badge>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
